/**
 * VGP Collection Geo-Filter Module
 * BUILD079 M4 - Location-Based Collection Filtering
 *
 * Replaces the standard Shopify product grid on geo-filtered collections
 * with products filtered by the customer's geographic region.
 *
 * Approach: Skeleton + AJAX (D016, Gemini-validated)
 * 1. Detects if current collection should be geo-filtered
 * 2. Replaces product grid with skeleton loading cards
 * 3. Calls /geo/collection-filter endpoint
 * 4. Renders filtered product cards
 *
 * Geo-filtered collections (configurable via Liquid template):
 * - landmark-puzzles
 * - snack-size-puzzles
 *
 * Products shown if assigned to customer's region (D008).
 * geo_mode=regional (default): Metro + State + National + untagged (D062, D070)
 * geo_mode=local: Metro + State only (D049)
 * Local mode excludes untagged; regional includes them (D070 overrides D063).
 *
 * @see BUILD079 - End-User Personalization Experience
 * @see D016 - Skeleton + AJAX frontend rendering
 */

(function() {
  'use strict';

  // BUILD079 M8: Use shared utilities
  if (typeof window.VGPProductUtils === 'undefined') {
    console.error('[VGPCollectionFilter] VGPProductUtils not loaded. Cannot initialize.');
    return;
  }

  var Utils = window.VGPProductUtils;

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  const CONFIG = {
    // API configuration
    proxyApiUrl: '/apps/vgp-wholesale',
    endpoint: '/geo/collection-filter',
    detectEndpoint: '/geo/detect',

    // Direct Edge Function URL — read from DOM at init time (Issue #6)
    // Set by Liquid template via data-vgp-direct-api-url attribute on collection page
    directApiUrl: null,

    // Selectors
    selectors: {
      collectionPage: '[data-vgp-collection-page]',
      productGrid: '[data-vgp-product-grid]',
      toolbar: '[data-vgp-collection-toolbar]',
      banner: '[data-vgp-collection-banner]',
    },

    // Products per page
    productsPerPage: 24,

    // Cache TTL (3 minutes)
    cacheTTLMs: 3 * 60 * 1000,

    // Request timeout
    timeoutMs: 8000,

    debug: true
  };

  // ============================================================================
  // UTILITY
  // ============================================================================

  function log(...args) {
    if (CONFIG.debug) console.log('[VGPCollectionFilter]', ...args);
  }

  function logError(...args) {
    console.error('[VGPCollectionFilter]', ...args);
  }

  function injectSkeletonStyles() {
    if (document.getElementById('vgp-collection-filter-styles')) return;

    const style = document.createElement('style');
    style.id = 'vgp-collection-filter-styles';
    style.textContent = `
      @keyframes vgp-coll-skeleton-pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 0.7; }
      }
      .vgp-skeleton {
        background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
        background-size: 200% 100%;
        animation: vgp-coll-skeleton-pulse 1.5s ease-in-out infinite;
      }
      .vgp-collection-empty-state {
        text-align: center;
        padding: 48px 24px;
        color: #666;
      }
      .vgp-collection-empty-state h3 {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 8px;
        color: #333;
      }
      .vgp-collection-empty-state p {
        font-size: 14px;
        margin: 0;
      }
    `;
    document.head.appendChild(style);

    // Inject shared dot-pulse spinner CSS (idempotent via id check)
    if (!document.getElementById('vgp-dot-pulse-css')) {
      const pulseStyle = document.createElement('style');
      pulseStyle.id = 'vgp-dot-pulse-css';
      pulseStyle.textContent = '.vgp-dot-pulse{position:relative;width:8px;height:8px;border-radius:50%;background:#999;animation:vgpDotPulse 1s ease-in-out infinite}.vgp-dot-pulse::before,.vgp-dot-pulse::after{content:"";position:absolute;width:8px;height:8px;border-radius:50%;background:#999;animation:vgpDotPulse 1s ease-in-out infinite}.vgp-dot-pulse::before{left:-16px;animation-delay:-0.2s}.vgp-dot-pulse::after{left:16px;animation-delay:0.2s}@keyframes vgpDotPulse{0%,100%{opacity:0.3;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}';
      document.head.appendChild(pulseStyle);
    }
  }

  // ============================================================================
  // CACHE
  // ============================================================================

  function getCached(handle, regionId, sort, geoMode) {
    var key = `vgp_coll_filter_${handle}_${regionId || 'none'}_${sort || 'default'}_${geoMode}`;
    return Utils.getCached(key, CONFIG.cacheTTLMs);
  }

  function setCache(handle, regionId, sort, geoMode, response) {
    var key = `vgp_coll_filter_${handle}_${regionId || 'none'}_${sort || 'default'}_${geoMode}`;
    Utils.setCache(key, response, CONFIG.cacheTTLMs);
  }

  // ============================================================================
  // PAGINATION STATE
  // ============================================================================

  /** Module-level state for infinite scroll pagination */
  const paginationState = {
    currentPage: 1,
    isLoadingMore: false,
    hasMore: false,
    handle: null,
    regionId: null,
    sort: null,
    geoMode: 'regional',
    gridElement: null,
    countElement: null,
    gridClasses: '',
    sentinelEl: null,
    observer: null,
  };

  /** AbortController for cancelling in-flight requests on re-init */
  let currentAbortController = null;

  // ============================================================================
  // MAIN MODULE
  // ============================================================================

  /**
   * Builds the base API URL for collection-filter requests.
   */
  function buildApiUrl(handle, regionId, page, sort, geoMode) {
    let url;
    if (Utils.hasWholesaleJWT()) {
      url = `${CONFIG.proxyApiUrl}${CONFIG.endpoint}?handle=${handle}&limit=${CONFIG.productsPerPage}`;
    } else if (CONFIG.directApiUrl) {
      url = `${CONFIG.directApiUrl}${CONFIG.endpoint}?handle=${handle}&limit=${CONFIG.productsPerPage}`;
    } else {
      log('Warning: No direct API URL configured, falling back to proxy');
      url = `${CONFIG.proxyApiUrl}${CONFIG.endpoint}?handle=${handle}&limit=${CONFIG.productsPerPage}`;
    }

    if (regionId) url += `&region_id=${regionId}`;
    if (page > 1) url += `&page=${page}`;
    if (sort && sort !== 'default') url += `&sort=${sort}`;
    url += `&geo_mode=${geoMode}`;

    return url;
  }

  async function initCollectionFilter() {
    // Abort any in-flight requests from a previous init (e.g., re-init on vgp:authenticated)
    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
    }

    injectSkeletonStyles();

    // Find the collection page
    const collectionPage = document.querySelector(CONFIG.selectors.collectionPage);
    if (!collectionPage) return;

    // Get the collection handle
    const handle = collectionPage.getAttribute('data-vgp-collection-handle');
    if (!handle) return;

    // Check if this collection should be geo-filtered (Issue #5: read from DOM, not hardcoded)
    const isGeoFiltered = collectionPage.getAttribute('data-vgp-geo-filtered') === 'true';
    if (!isGeoFiltered) {
      log('Collection', handle, 'not geo-filtered, skipping');
      return;
    }

    // Read direct API URL from DOM (Issue #6: no hardcoded infrastructure URLs in JS)
    CONFIG.directApiUrl = collectionPage.getAttribute('data-vgp-direct-api-url') || null;

    // Read sort from URL params (D030: sort passthrough)
    // Map Shopify's sort_by values to our backend sort values
    const urlParams = new URLSearchParams(window.location.search);
    const rawSort = urlParams.get('sort_by') || '';
    const SORT_MAP = {
      'best-selling': 'best-selling',
      'title-ascending': 'title-asc',
      'title-descending': 'title-desc',
      'price-ascending': 'price-asc',
      'price-descending': 'price-desc',
      'created-descending': 'created-desc',
    };
    const sort = SORT_MAP[rawSort] || 'best-selling';

    // D062: geo_mode param for two-tier collection model
    // 'local' = metro + state only ("Local Landmarks"), 'regional' = metro + state + national (default)
    const geoModeParam = urlParams.get('geo_mode');
    const geoMode = (geoModeParam === 'local' || geoModeParam === 'regional') ? geoModeParam : 'regional';

    log('Initializing geo-filter for collection:', handle, 'sort:', sort, 'geoMode:', geoMode);

    // Find the product grid
    const productGrid = collectionPage.querySelector(CONFIG.selectors.productGrid);
    if (!productGrid) return;

    // Hide pagination (we'll manage our own)
    const pagination = collectionPage.querySelector('.pagination');
    if (pagination) pagination.style.display = 'none';

    // Hide the product count in toolbar (will update with filtered count)
    const countEl = collectionPage.querySelector('.collection-toolbar__count');

    // Create AbortController for this init cycle
    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    // Reset pagination state
    paginationState.currentPage = 1;
    paginationState.isLoadingMore = false;
    paginationState.hasMore = false;
    paginationState.handle = handle;
    paginationState.regionId = null;
    paginationState.sort = sort;
    paginationState.geoMode = geoMode;
    paginationState.gridElement = productGrid;
    paginationState.countElement = countEl;
    paginationState.gridClasses = productGrid.className;

    // Replace grid content with skeletons
    productGrid.innerHTML = Utils.createSkeletonGrid(8);
    log('Showing skeleton loading state');

    try {
      // Detect region (with timeout)
      const regionId = await Promise.race([
        Utils.detectRegion(CONFIG.proxyApiUrl, CONFIG.detectEndpoint),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000))
      ]);

      // Bail out if aborted during region detection
      if (signal.aborted) return;

      paginationState.regionId = regionId;

      // Check cache (includes sort and geoMode in key)
      const cached = getCached(handle, regionId, sort, geoMode);
      if (cached) {
        log('Cache hit for', handle);
        renderResults(productGrid, countEl, cached, paginationState.gridClasses);
        return;
      }

      // Build API URL
      const url = buildApiUrl(handle, regionId, 1, sort, geoMode);
      log('Fetching filtered products from:', url);

      // Use the init-cycle AbortController signal; add a timeout that aborts it
      const timeoutId = setTimeout(() => { if (!signal.aborted) currentAbortController?.abort(); }, CONFIG.timeoutMs);
      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: Utils.buildAuthHeaders(),
          signal: signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      log('Got', data.products?.length, 'products, total:', data.pagination?.total);

      // Cache the result (includes sort and geoMode in key)
      setCache(handle, regionId, sort, geoMode, data);

      // Render the results
      renderResults(productGrid, countEl, data, paginationState.gridClasses);

    } catch (err) {
      // Don't show error for intentional aborts (re-init)
      if (err.name === 'AbortError') {
        log('Request aborted (re-init)');
        return;
      }
      logError('Collection filter failed:', err.message);
      productGrid.innerHTML = `
        <div class="vgp-collection-empty-state" style="grid-column: 1 / -1;">
          <h3>Unable to load products</h3>
          <p>Unable to load products. Please try refreshing the page.</p>
        </div>
      `;
    }
  }

  /**
   * Renders the filtered product results into the grid.
   */
  function renderResults(gridElement, countElement, data, originalClasses) {
    const products = data.products || [];
    const pagination = data.pagination || {};

    if (products.length === 0 && (!pagination.total || pagination.total === 0)) {
      // D028/D047: Differentiated empty state for local mode
      const isLocal = paginationState.geoMode === 'local';
      const emptyHtml = isLocal
        ? `<div class="vgp-collection-empty-state" style="grid-column: 1 / -1;">
            <h3>No local landmarks for your area</h3>
            <p>We don't have local landmark puzzles for your region yet.</p>
            <p style="margin-top: 12px;"><a href="/collections/landmark-puzzles" style="color: #00AEFF; text-decoration: underline;">Browse all Landmark Puzzles</a></p>
          </div>`
        : `<div class="vgp-collection-empty-state" style="grid-column: 1 / -1;">
            <h3>No local puzzles available</h3>
            <p>No local puzzles available for your region in this collection.</p>
          </div>`;
      gridElement.innerHTML = emptyHtml;
      if (countElement) countElement.textContent = '0 products';
      paginationState.hasMore = false;
      teardownInfiniteScroll();
      return;
    }

    // Render product cards
    gridElement.className = originalClasses;
    gridElement.innerHTML = products.map(function(p) { return Utils.renderProductCard(p); }).join('');

    // Update count
    if (countElement) {
      const total = pagination.total || products.length;
      countElement.textContent = `${total} ${total === 1 ? 'product' : 'products'}`;
    }

    // Update pagination state and setup/teardown infinite scroll
    paginationState.hasMore = pagination.has_more === true;
    if (paginationState.hasMore) {
      setupInfiniteScroll(gridElement);
    } else {
      teardownInfiniteScroll();
    }

    log('Rendered', products.length, 'filtered products, hasMore:', paginationState.hasMore);

    // Dispatch event for other modules
    window.dispatchEvent(new CustomEvent('vgp:collection-filtered', {
      detail: {
        handle: data.collection_handle,
        region: data.region,
        productCount: products.length,
        total: pagination.total
      }
    }));
  }

  // ============================================================================
  // LOAD MORE
  // ============================================================================

  /**
   * Sets up the infinite scroll sentinel and observer below the product grid.
   */
  function setupInfiniteScroll(gridElement) {
    teardownInfiniteScroll();

    const sentinel = document.createElement('div');
    sentinel.className = 'vgp-infinite-scroll-sentinel';
    sentinel.style.height = '1px';
    gridElement.parentNode.insertBefore(sentinel, gridElement.nextSibling);
    paginationState.sentinelEl = sentinel;

    const obs = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting && paginationState.hasMore && !paginationState.isLoadingMore) {
        loadMoreProducts();
      }
    }, { rootMargin: '0px 0px 200px 0px' });
    obs.observe(sentinel);
    paginationState.observer = obs;
  }

  /**
   * Tears down the infinite scroll sentinel and observer.
   */
  function teardownInfiniteScroll() {
    if (paginationState.observer) {
      paginationState.observer.disconnect();
      paginationState.observer = null;
    }
    if (paginationState.sentinelEl) {
      paginationState.sentinelEl.remove();
      paginationState.sentinelEl = null;
    }
  }

  function showScrollSpinner() {
    if (paginationState.sentinelEl) {
      paginationState.sentinelEl.innerHTML = '<div style="display:flex;justify-content:center;padding:24px 0;">' +
        '<div class="vgp-dot-pulse"></div></div>';
    }
  }

  function hideScrollSpinner() {
    if (paginationState.sentinelEl) {
      paginationState.sentinelEl.innerHTML = '';
    }
  }

  /**
   * Loads the next page of products (triggered by IntersectionObserver).
   */
  async function loadMoreProducts() {
    if (paginationState.isLoadingMore || !paginationState.hasMore) return;

    paginationState.isLoadingMore = true;
    showScrollSpinner();

    try {
      const nextPage = paginationState.currentPage + 1;
      const url = buildApiUrl(paginationState.handle, paginationState.regionId, nextPage, paginationState.sort, paginationState.geoMode);

      log('Loading more, page:', nextPage);

      const loadMoreAbort = new AbortController();
      const timeoutId = setTimeout(() => loadMoreAbort.abort(), CONFIG.timeoutMs);
      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: Utils.buildAuthHeaders(),
          signal: loadMoreAbort.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const products = data.products || [];
      const pagination = data.pagination || {};

      if (products.length > 0) {
        const gridElement = paginationState.gridElement;
        const newCardsHtml = products.map(function(p) { return Utils.renderProductCard(p); }).join('');
        gridElement.insertAdjacentHTML('beforeend', newCardsHtml);

        paginationState.currentPage = nextPage;
        log('Appended', products.length, 'products for page', nextPage);
      }

      paginationState.hasMore = pagination.has_more === true;

      hideScrollSpinner();
      if (!paginationState.hasMore) {
        teardownInfiniteScroll();
      }

    } catch (err) {
      logError('Load more failed:', err.message);
      hideScrollSpinner();
    } finally {
      paginationState.isLoadingMore = false;
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  function initWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(initCollectionFilter, 150);
      });
    } else {
      setTimeout(initCollectionFilter, 150);
    }
  }

  // Re-initialize when auth completes
  window.addEventListener('vgp:authenticated', () => {
    log('Auth event received, re-initializing collection filter');
    // Clear cached responses so re-fetch uses authenticated segment
    try {
      Object.keys(sessionStorage).forEach(function(key) {
        if (key.indexOf('vgp_coll_filter_') === 0) sessionStorage.removeItem(key);
      });
    } catch(e) {}
    initCollectionFilter();
  });

  initWhenReady();

  log('Module loaded');

})();
